import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Users } from 'lucide-react';
import { useEvent, useEvents } from '../hooks/useEvents';
import { SearchableListSelect } from '../components/common';
import { GuestListView } from '../components/guests/list/GuestListView';
import { useInEventWorkspace } from '../components/events/workspace/EventWorkspaceContext';

/**
 * The guest list, at `/events/:eventId/guests` (the workspace's Guests tab)
 * and at `/guests`. A guest belongs to one event and everything on the list —
 * its numbers, seating, RSVPs — is the event's, so `/guests` asks which event
 * first and keeps the answer in the address (`?event=`).
 */
export function GuestListPage() {
  const { eventId: routeEventId } = useParams<{ eventId: string }>();
  const [params, setParams] = useSearchParams();
  const inWorkspace = useInEventWorkspace();
  const navigate = useNavigate();
  const eventId = routeEventId ?? params.get('event') ?? '';
  const { data: event } = useEvent(eventId);
  const { data: events = [], isLoading: eventsLoading } = useEvents();

  return (
    <div className="space-y-4">
      {routeEventId && !inWorkspace && (
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          data-testid="back-btn"
        >
          <ArrowLeft size={16} aria-hidden />
          {event ? `Back to ${event.name}` : 'Back to event'}
        </button>
      )}
      {!routeEventId && (
        <div className="max-w-md">
          <label className="block text-sm font-medium text-gray-700 mb-1">Event</label>
          <SearchableListSelect
            items={events}
            value={eventId || null}
            onChange={(value) => {
              const next = new URLSearchParams();
              if (value) next.set('event', value as string);
              setParams(next, { replace: true });
            }}
            getValue={(e) => e.id}
            getLabel={(e) => e.name}
            getSubtitle={(e) => new Date(e.startAt).toLocaleDateString()}
            placeholder="Choose an event"
            ariaLabel="Event"
            isLoading={eventsLoading}
          />
        </div>
      )}
      {eventId ? (
        <GuestListView
          key={eventId}
          eventId={eventId}
          title={
            routeEventId && inWorkspace ? 'Guests' : event ? `Guests · ${event.name}` : 'Guests'
          }
        />
      ) : (
        <div className="rounded-2xl border border-gray-200 bg-white p-10 text-center">
          <Users className="mx-auto h-10 w-10 text-gray-300" aria-hidden />
          <p className="mt-2 text-gray-600">Choose an event to see its guests.</p>
        </div>
      )}
    </div>
  );
}
